import type {
  ILocale,
  LocalizedSiteTree,
  ILocalizedSite
} from '@/constructs/LocaleSchema';
import type { Nullable } from '@/type-utils';

import LocaleSelectionService from '@/services/isomorphic/LocaleSelectionService';
import I18NService, { getCountryName } from '@/services/isomorphic/I18NService';

import { getHook } from '@/services/utils/react-utils/hook-utils';
import type { NextRouter } from 'next/router';
import { InvalidArgumentError, InvalidStateError } from '@/utils/errors';
import { Region } from '@/constructs/Region';
import { action, computed, makeObservable, observable } from 'mobx';

import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import { getFlagByCountry } from '@/react/components/core-ui/Icon/getFlagByCountry';
import LoggerService from '@/services/isomorphic/LoggerService';
import type { ILocaleSite } from './ILocaleSite';
import {
  matchByCurrentAlias,
  matchByAllAliases,
  matchByURLPath
} from './matchers';

/**
 * View-model that enables locale querying and selection. This view-model powers
 * the various locale selectors in the site.
 *
 * @see The guide at `@/docs/guides/i18n.md` for more info on localization.
 */
export default class LocaleListVM {
  /** @inheritdoc */
  public constructor() {
    makeObservable(this);
  }

  @observable private _dedupCountries: boolean = false;
  @observable private _filterString?: Nullable<string> = null;

  private _allLocalizedSites: Nullable<ReadonlyArray<ILocalizedSite>> = null;

  /**
   * Gets the list of all available locales selections.
   * @returns An array of `ILocalizedSite` objects.
   */
  @computed public get allLocalizedSites(): ReadonlyArray<ILocalizedSite> {
    this._allLocalizedSites ??= LocaleSelectionService.list();
    return this._allLocalizedSites;
  }

  /**
   * Transforms a locale selection into a localized site.
   * @param site - The locale selection to transform.- The site to transform.
   * @param useSiteLocale - If `true`, the locale of the site will be used.
   * @returns The transformed localized site.
   */
  private transformLocaleList(
    site: ILocalizedSite,
    useSiteLocale = false
  ): ILocaleSite {
    const icon = getFlagByCountry(site.countryID);

    const name =
      site.aliases[
        useSiteLocale
          ? site.locale.language
          : I18NService.currentLocale.language
      ] ?? getCountryName(site.countryID);

    return {
      id: site.id,
      name,
      regionID: site.regionID,
      countryID: site.countryID,
      locale: site.locale,
      icon,
      url: site.url
    } as ILocaleSite;
  }

  /**
   * Given a site and a filter string, calculate a value representing how much
   * the aforementioned filter string matches the provided site.
   *
   * Lower values mean higher match priority, and should be rendered first.
   *
   * Aliases and URL pathnames will be factored in when calculating these
   * values.
   *
   * @param site - The site to search on.
   * @param filterString - The filter string to search.
   *
   * @returns A match value for the provided site and filter string, or `-1` if
   * the filter string does not match the site at all.
   */
  private getFilterMatchValue(
    site: ILocalizedSite,
    filterString: string
  ): number {
    /** All the matchers to use, ordered from lowest to highest priority. */
    const matchers = [matchByCurrentAlias, matchByAllAliases, matchByURLPath];

    /** A value that will increment when a lower priority matcher is used. */
    let priorityScore = 0;

    // For every configured matcher...
    for (const matcher of matchers) {
      // Run it and find the match value.
      const matchValue = matcher(site, filterString);

      // If the value is -1, there is no match. Continue.
      if (matchValue >= 0) {
        return matchValue + priorityScore;
      }

      // Add 100 for each matcher that didn't yield a result. This will result
      // in sites using lower priority matchers getting higher scores.
      priorityScore += 100;
    }

    // If none of the matchers work, there was no match at all. Return -1.
    return -1;
  }

  /**
   * Checks if the given site has duplicates in the provided list of sites.
   * @param site - The site to check for duplicates.
   * @param sites - The list of sites to check against.
   * @returns `true` if the site has duplicates in the provided list, and
   * `false` otherwise.
   */
  private hasDuplicates(
    site: ILocalizedSite,
    sites: ReadonlyArray<ILocalizedSite>
  ): boolean {
    return sites.filter((s) => s.countryID === site.countryID).length > 1;
  }

  /**
   * Gets all sites that match the current filter string. Use the
   * {@link filter} method to refine the results of this property.
   *
   * @returns All sites that match the current filter, as an array of
   * {@link ILocalizedSite} objects.
   */
  @computed
  public get sites(): ReadonlyArray<ILocaleSite> {
    const hits: Array<ILocalizedSite> | Array<[ILocalizedSite, number]> = [];

    for (const site of this.allLocalizedSites) {
      const hasDuplicates = this.hasDuplicates(site, this.allLocalizedSites);
      const currentAlias = site.aliases[I18NService.currentLocale.language];

      if (isNullish(currentAlias)) {
        LoggerService.warn(
          `Cannot list site "${site.id}" as it does not have a valid alias ` +
            `for the current language.`
        );

        continue;
      }

      if (this._dedupCountries && hasDuplicates) {
        continue;
      }

      if (!isNullish(this._filterString)) {
        const matchValue = this.getFilterMatchValue(site, this._filterString);

        if (matchValue >= 0) {
          (hits as Array<[ILocalizedSite, number]>).push([site, matchValue]);
        } else {
          continue;
        }
      } else {
        (hits as Array<ILocalizedSite>).push(site);
      }
    }

    if (hits.length > 0 && Array.isArray(hits[0])) {
      // hits is an array of tuples. sort and map
      return (hits as Array<[ILocalizedSite, number]>)
        .sort(([siteA, valueA], [siteB, valueB]) => valueA - valueB)
        .map(([site, _]) => {
          return this.transformLocaleList(
            site,
            this.hasDuplicates(site, this.allLocalizedSites)
          );
        });
    }

    // Hits is either an empty array or an array of sites already.
    return hits.map((hit) =>
      this.transformLocaleList(
        hit as ILocalizedSite,
        this.hasDuplicates(hit as ILocalizedSite, this.allLocalizedSites)
      )
    );
  }

  /**
   * Filters the {@link sites available sites} with the provided filter options.
   * Will not return anything. Components should relying instead on the computed
   * `sites` property instead.
   *
   * @param options - The options to filter by,.
   * @param update -  If `true`, the options passed to the current call will be
   * spread on top of the existing ones instead of replacing them entirely.
   * Defaults to `true`.
   * @param filterString
   */
  @action
  public filter(filterString: Nullable<string>): void {
    this._filterString = filterString;
  }

  /**
   * Clears all the applied filters.
   *
   * @see The {@link filter} method.
   */
  @action
  public clearFilter(): void {
    this._filterString = null;
  }

  /**
   * Returns the {@link Region regions} included in the current {@link sites}
   * array.
   *
   * @returns The regions, as an array of {@link Region} enum members.
   */
  @computed public get regions(): ReadonlyArray<Region> {
    const regionsInList = new Set<Region>();

    for (const site of this.sites) {
      regionsInList.add(site.regionID);
    }

    return Array.from(regionsInList).sort();
  }

  /**
   * Given a region, find all sites included in the current {@link sites} array
   * that belong to it.
   *
   * @param regionID - Region to filter by.
   * @returns The matching sites.
   */
  public sitesByRegion(regionID: Region): ReadonlyArray<ILocaleSite> {
    return this.sites
      .filter((site) => !isNullish(regionID) && site.regionID === regionID)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * All the localized sites available, as a {@link LocalizedSiteTree}.
   * Useful for listing all locales by region and country.
   *
   * @returns All the localized sites available, as a {@link LocalizedSiteTree}.
   */
  public get siteTree(): LocalizedSiteTree {
    return LocaleSelectionService.getAllLocalizedSites();
  }

  /**
   * Lists all {@link Region regions} that have selectable {@link ILocalizedSite sites}.
   * Will ignore current filter options.
   *
   * @returns An array with all {@link Region regions} that have selectable {@link ILocalizedSite sites.}.
   */
  public get allRegions(): ReadonlyArray<Region> {
    return Object.keys(this.siteTree) as Array<Region>;
  }

  /**
   * Custom type guard that determines if a param passed to {@link getSiteURL}
   * is an {@link ILocale} object.
   *
   * @param site  - Parameter to test.
   *
   * @returns `true` if the provided value is indeed an {@link ILocale} object,
   * and `false` otherwise.
   */
  private siteParamIsLocale(
    site: string | ILocalizedSite | ILocale
  ): site is ILocale {
    const asLocale = site as ILocale;
    return asLocale.language !== undefined && asLocale.language !== undefined;
  }

  /**
   * Custom type guard that determines if a param passed to {@link getSiteURL}
   * is an {@link ILocalizedSite} object.
   *
   * @param site  - Parameter to test.
   *
   * @returns `true` if the provided value is indeed an {@link ILocalizedSite}
   * object, and `false` otherwise.
   */
  private siteParamIsLocalizedSite(
    site: string | ILocalizedSite | ILocale
  ): site is ILocalizedSite {
    const asLocalizedSite = site as ILocalizedSite;
    return (
      asLocalizedSite.id !== undefined &&
      asLocalizedSite.regionID !== undefined &&
      asLocalizedSite.countryID !== undefined
    );
  }

  /**
   * Provided a {@link ILocalizedSite.id site id}, retrieve the URL of the
   * corresponding {@link ILocalizedSite localized site}.
   *
   * @param siteID - Site ID to retrieve the url for.
   *
   * @returns The URL of the corresponding site.
   */
  public getSiteURL(siteID: string): URL;

  /**
   * Provided a {@link ILocalizedSite localized site}, retrieve its
   * corresponding URL.
   *
   * @param localizedSite - A localized site to retrieve the URL for.
   *
   * @returns The URL of the corresponding site.
   */
  public getSiteURL(localizedSite: ILocalizedSite): URL;

  /**
   * Provided a {@link ILocale locale object}, retrieve the URL of the
   * corresponding {@link ILocalizedSite localized site}.
   *
   * @param locale - The locale to retrieve the URL for.
   *
   * @returns The URL of the corresponding site.
   */
  public getSiteURL(locale: ILocale): URL;

  /**
   * Provided a {@link ILocalizedSite.id site id}, a
   * {@link ILocalizedSite localized site}, or a {@link ILocale locale object},
   * retrieve the URL of the corresponding site.
   *
   * @param site - The site to retrieve the URL for.
   *
   * @returns The URL of the corresponding site.
   */
  public getSiteURL(site: string | ILocalizedSite | ILocale): URL;

  /**
   * Provided a {@link ILocalizedSite.id site id}, a
   * {@link ILocalizedSite localized site}, or a {@link ILocale locale object},
   * retrieve the URL of the corresponding site.
   *
   * @param site - The site to retrieve the URL for.
   *
   * @returns The URL of the corresponding site.
   *
   * @throws An {@link InvalidArgumentError} if passed a site that is either not
   * configured or malformed.
   */
  public getSiteURL(site: string | ILocalizedSite | ILocale): URL {
    // TODO: As per Nick's request, implement a system where one can map pages
    // from one localized site to pages others. Add ticket here if it exists.
    switch (true) {
      case typeof site === 'string': {
        // `site` is a site ID.
        const matchedSite = this.allLocalizedSites.find(
          (localizedSite) => localizedSite.id === site
        );

        if (!matchedSite) {
          throw new InvalidArgumentError();
        }

        return matchedSite.url;
      }

      case this.siteParamIsLocale(site): {
        const matchedSite = this.allLocalizedSites.find(
          ({ locale: { country, language } }) =>
            country === site.country && language === site.language
        );

        if (!matchedSite) {
          throw new InvalidArgumentError(
            `Could not get the site URL for locale "${site.toString()}": This` +
              ' locale does not have a site configured for it.'
          );
        }

        return matchedSite.url;
      }

      case this.siteParamIsLocalizedSite(site): {
        return site.url;
      }

      default:
        throw new InvalidArgumentError(
          `Cannot get site URL: site parameter is malformed.`
        );
    }
  }

  /**
   * Provided a {@link ILocalizedSite.id site id}, navigate to the corresponding
   * {@link ILocalizedSite localized site}.
   *
   * @param siteID - Site ID to navigate to.
   */
  public select(siteID: string): void;

  /**
   * Provided a {@link ILocalizedSite localized site}, navigate to its
   * corresponding URL.
   *
   * @param localizedSite - A localized site to navigate to.
   */
  public select(localizedSite: ILocalizedSite): void;

  /**
   * Provided a {@link ILocale locale object}, navigate to the corresponding
   * {@link ILocalizedSite localized site}.
   *
   * @param locale - The locale to navigate to.
   */
  public select(locale: ILocale): void;

  /**
   * Provided a {@link ILocalizedSite.id site id}, a
   * {@link ILocalizedSite localized site}, or a {@link ILocale locale object},
   * navigate to the corresponding site.
   *
   * @param site - The site to navigate to.
   */
  public select(site: string | ILocalizedSite | ILocale): void;

  /**
   * Provided a {@link ILocalizedSite.id site id}, a
   * {@link ILocalizedSite localized site}, or a {@link ILocale locale object},
   * navigate to the corresponding site.
   *
   * @param site - The site to navigate to.
   */
  public select(site: string | ILocalizedSite | ILocale): void {
    const router = getHook<NextRouter>('Router');
    const url = this.getSiteURL(site);

    router.push(url);
  }

  /**
   * If entries should be deduplicated by country.
   *
   * Would only have an effect on countries that support more than one
   * language. Only an entry with the default language of the country will
   * be included in the array.
   */
  public set dedupCountries(value: boolean) {
    this._dedupCountries = value;
  }

  /**
   * If entries should be deduplicated by country.
   *
   * Would only have an effect on countries that support more than one
   * language. Only an entry with the default language of the country will
   * be included in the array.
   *
   * @returns `true` if country deduplication is enabled, and `false` otherwise.
   */
  @computed public get dedupCountries(): boolean {
    return this._dedupCountries;
  }

  /**
   * Gets the default country data from the locale list.
   * @returns An array of `ILocaleSite` objects to render locale links.
   * @throws If the locale list is missing one of the default countries.
   */
  @computed
  public get defaultLocaleList(): Array<ILocaleSite> {
    const defaultCountries =
      ConfigurationService.getConfig('locale').getSetting(
        'defaultCountries'
      ).value;

    const defaultList = defaultCountries.map((code) => {
      const selection = this.sites.find((item) => item.countryID === code);

      if (isNullish(selection)) {
        throw new InvalidStateError(
          `Cannot get default locale list - locale list is missing ${code}.`
        );
      }

      return selection;
    });

    return defaultList;
  }

  /**
   * Gets the filtered sites list or the default list if there is no filter
   * string.
   * @returns An array of `ILocaleSite` objects that match the current filter
   * string or the default list.
   */
  @computed
  public get filteredSites(): ReadonlyArray<ILocaleSite> {
    if (isNullOrEmpty(this._filterString)) {
      return this.defaultLocaleList;
    }

    return this.sites;
  }
}
